import 'react-multi-carousel/lib/styles.css';

import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Container,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Skeleton,
  Stack,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';
import { forwardRef, useEffect, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Card from 'components/Card';
import CodePreviewCard from 'components/CodePreviewCard';
import { EmptyContent } from 'components/EmptyContent';
import {
  getWelcomeModalStorageKey,
  shouldShowWelcomeModal,
  WelcomeModal,
} from 'components/Modal/WelcomeModal';
import { SEO } from 'components/SEO';
import Tabs from 'components/Tabs';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { CodeEditorType } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

import Discord from './Discord';
import Feed from './Feed';
import JourneyDashboard from './JourneyDashboard';
import Leaderboards from './Leaderboards';
import UnlockBIG from './UnlockBIG';

// Wrapper to filter out non-DOM props (e.g. rtl) that react-multi-carousel passes to custom arrows
const CarouselArrow = forwardRef<HTMLButtonElement, any>(({ rtl, carouselState, ...rest }, ref) => (
  <IconButton ref={ref} aria-label="Carousel arrow" {...rest} />
));

const responsiveCarousel = (dataLength: number) => ({
  desktop: {
    breakpoint: { max: 10000, min: 1536 },
    items: 2,
    partialVisibilityGutter: dataLength === 2 ? 0 : 160,
    slidesToSlide: dataLength === 2 ? 1 : 2,
  },
  tablet: {
    breakpoint: { max: 1536, min: 960 },
    items: 2,
    partialVisibilityGutter: dataLength === 2 ? 0 : 80,
    slidesToSlide: dataLength === 2 ? 1 : 2,
  },
  mobile: {
    breakpoint: { max: 960, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
});

const Dashboard = () => {
  const { isDesktopNavigation } = useMediaQueries();
  const [dashboardView, setDashboardView] = useState<'journey' | 'community'>('journey');
  const [tabIndex, setTabIndex] = useState(0);
  const { id: currentUserId, isFree, plan } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const welcomeModal = useDisclosure();

  // Welcome modal — shown once after onboarding payment
  useEffect(() => {
    const hasWelcomeParam = searchParams.get('welcome') === 'true';
    const storageKey = getWelcomeModalStorageKey(currentUserId);
    const alreadyShown = !!localStorage.getItem(storageKey);

    if (shouldShowWelcomeModal(hasWelcomeParam, alreadyShown)) {
      welcomeModal.onOpen();
    }
  }, []);

  const handleWelcomeClose = () => {
    localStorage.setItem(getWelcomeModalStorageKey(currentUserId), 'true');
    welcomeModal.onClose();
    // Clean up URL params
    setSearchParams((prev) => {
      prev.delete('welcome');
      prev.delete('trial');
      return prev;
    }, { replace: true });
    // Scroll to the bottom of the page (where START / Section 1 of the journey is)
    // Delay to allow Chakra modal exit animation to complete and unblock scroll
    setTimeout(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      });
    }, 500);
  };

  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();

  const { data, isFetching } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetUserCode, currentUserId, tabIndex],
    queryFn: async () => {
      const userCodeViewQuery = supabase
        .from('user_code_view')
        .select('*')
        .not('user_id', 'is', null)
        .limit(20);

      if (tabIndex === 0) {
        const oneWeekAgo = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7);
        const formattedDate = oneWeekAgo.toLocaleString('en-US', { timeZone: 'UTC' });

        userCodeViewQuery
          .gte('completed_date', formattedDate)
          .order('total_score', { ascending: false })
          .eq('is_private', false)
          .order('completed_date', { ascending: false });
      }

      if (tabIndex === 1) {
        userCodeViewQuery
          .order('total_score', { ascending: false })
          .eq('is_private', false)
          .order('completed_date', { ascending: false });
      }

      if (tabIndex === 2) {
        userCodeViewQuery
          .eq('user_id', currentUserId)
          .order('completed_date', { ascending: false });
      }

      const { data, error } = await userCodeViewQuery;

      if (error) throw error;

      return data;
    },
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: [TanstackQueryName.GetUserCode, currentUserId, 0],
      });
      queryClient.removeQueries({
        queryKey: [TanstackQueryName.GetUserCode, currentUserId, 1],
      });
      queryClient.removeQueries({
        queryKey: [TanstackQueryName.GetUserCode, currentUserId, 2],
      });
    };
  }, []);

  const renderUserCode = () => {
    if (!data || isFetching) {
      if (!isDesktopNavigation) {
        return <Skeleton height="531px" width="100%" />;
      }

      return (
        <HStack spacing={4}>
          <Skeleton height="531px" width="100%" />
          <Skeleton height="531px" width="100%" />
          <Skeleton height="531px" width="50%" />
        </HStack>
      );
    }

    if (data.length === 0) {
      return (
        <Center height="531px">
          <EmptyContent type="dashboard" mt="0px" />
        </Center>
      );
    }

    const getInvalidateKeys = (
      type: CodeEditorType,
      userId: string,
      parentId: string,
      solutionUrl: string
    ) => {
      const baseKeys = [[TanstackQueryName.GetProfileCode, userId, type]];

      if (type === 'project') {
        return [
          ...baseKeys,
          [TanstackQueryName.GetProjectSolutions, parentId],
          [TanstackQueryName.GetProjectSolution, solutionUrl],
        ];
      }

      if (type === 'challenge') {
        return [
          ...baseKeys,
          [TanstackQueryName.GetChallengeSolutions, parentId],
          [TanstackQueryName.GetChallengeSolution, solutionUrl],
        ];
      }

      if (type === 'problem') {
        return [
          ...baseKeys,
          [TanstackQueryName.GetPracticeProblemsSolutions, parentId],
          [TanstackQueryName.GetPracticeProblemSolution, solutionUrl],
        ];
      }

      if (type === 'code-frame') {
        return [
          ...baseKeys,
          [TanstackQueryName.GetCodeFrames, currentUserId],
          [TanstackQueryName.GetCodeFrames, null],
          [TanstackQueryName.GetCodeFrame, solutionUrl],
        ];
      }

      if (type === 'prototype') {
        return [
          ...baseKeys,
          [TanstackQueryName.GetPrototypes, currentUserId],
          [TanstackQueryName.GetPrototypes, null],
          [TanstackQueryName.GetPrototype, solutionUrl],
        ];
      }

      return baseKeys;
    };

    return (
      <Carousel
        responsive={responsiveCarousel(data.length)}
        partialVisible
        swipeable
        arrows
        customLeftArrow={
          <CarouselArrow
            aria-label="Swipe left"
            width="48px"
            height="64px"
            backgroundColor="gray.400"
            position="absolute"
            top="50%"
            left="32px"
            _hover={{ backgroundColor: 'gray.500' }}
          >
            <ChevronLeftIcon boxSize={8} color="black" />
          </CarouselArrow>
        }
        customRightArrow={
          <CarouselArrow
            aria-label="Swipe right"
            width="48px"
            height="64px"
            backgroundColor="gray.400"
            position="absolute"
            top="50%"
            right={isDesktopNavigation ? '16px' : '32px'}
            _hover={{ backgroundColor: 'gray.500' }}
          >
            <ChevronRightIcon boxSize={8} color="black" />
          </CarouselArrow>
        }
      >
        {data.map((userCode) => (
          <Box key={userCode.id} mr={4} height="100%">
            <CodePreviewCard
              width={`calc(100% + ${isDesktopNavigation ? '0px' : '16px'})`}
              containerHeight="531px"
              isMySolution={false}
              canEdit={false}
              queryKey={[TanstackQueryName.GetUserCode, currentUserId, tabIndex]}
              invalidateKeys={getInvalidateKeys(
                userCode.type as CodeEditorType,
                userCode.user_id!,
                userCode.parent_id!,
                userCode.solution_url!
              )}
              {...userCode}
            />
          </Box>
        ))}
      </Carousel>
    );
  };

  return (
    <>
      <WelcomeModal
        isOpen={welcomeModal.isOpen}
        onClose={handleWelcomeClose}
        plan={plan}
        isTrial={searchParams.get('trial') === 'true'}
      />
      <SEO
        title="BigDevSoon | Dashboard"
        description="Browse user's creations, community feed, and leaderboards."
        url={`${process.env.REACT_APP_SITE_URL}Dashboard`}
      />
      <Container maxW="1400px" pt="28px">
        {/* Dashboard view switcher */}
        <HStack spacing={1} mb={6} bg="whiteAlpha.50" p={1} borderRadius="lg" w="fit-content">
          <Box
            as="button"
            px={4}
            py={2}
            borderRadius="md"
            bg={dashboardView === 'journey' ? 'purple.500' : 'transparent'}
            color={dashboardView === 'journey' ? 'white' : undefined}
            fontWeight={dashboardView === 'journey' ? 'semibold' : 'normal'}
            fontSize="sm"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ bg: dashboardView === 'journey' ? 'purple.500' : 'whiteAlpha.200' }}
            onClick={() => setDashboardView('journey')}
          >
            My Journey
          </Box>
          <Box
            as="button"
            px={4}
            py={2}
            borderRadius="md"
            bg={dashboardView === 'community' ? 'purple.500' : 'transparent'}
            color={dashboardView === 'community' ? 'white' : undefined}
            fontWeight={dashboardView === 'community' ? 'semibold' : 'normal'}
            fontSize="sm"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ bg: dashboardView === 'community' ? 'purple.500' : 'whiteAlpha.200' }}
            onClick={() => setDashboardView('community')}
          >
            Community
          </Box>
        </HStack>

        {dashboardView === 'journey' ? (
          <JourneyDashboard />
        ) : (
          <Grid templateColumns="repeat(12, 1fr)" gap={4}>
            <GridItem colSpan={12}>
              <Card
                header={
                  <Tabs
                    tabs={['Trending', 'Most Popular', 'Your Work']}
                    tabIndex={tabIndex}
                    onChange={(index) => setTabIndex(index)}
                  />
                }
                position="relative"
                width="100%"
              >
                {renderUserCode()}
              </Card>
            </GridItem>
            <GridItem colSpan={[12, 12, 12, 12, 8]}>
              <Feed />
            </GridItem>
            <GridItem colSpan={[12, 12, 12, 12, 4]}>
              <Stack spacing={4} alignItems="center">
                {isFree && <UnlockBIG />}
                <Leaderboards />
                <Discord />
              </Stack>
            </GridItem>
          </Grid>
        )}
      </Container>
    </>
  );
};

export default Dashboard;
